import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import styled from 'styled-components'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded'
import { toast } from 'react-toastify'
import { updateTask, submitTask } from 'services/taskService'
import FormTemplateField from './FormTemplateField'
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded'
import GenerateFormFromDocument, { FormGenButton } from './GenerateFormFromDocument'

const AssignFormPanel = forwardRef(({ taskId, canManage, onAssigned, canSubmit = false, requiresApproval = false, buildingPassportId, programmeObligationId }, ref) => {
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [selected, setSelected] = useState('')
  const [saving, setSaving] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const panelRef = useRef(null)

  useImperativeHandle(ref, () => ({
    requestSubmit: () => {
      if (!canSubmit) return false
      setConfirming(true)
      if (panelRef.current && typeof panelRef.current.scrollIntoView === 'function') {
        panelRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
      return true
    }
  }), [canSubmit])

  const assign = async () => {
    if (!selected) { toast.info('Choose a form first.'); return }
    setSaving(true)
    try {
      await updateTask({ id: taskId, formTemplateId: selected })
      toast.success('Form assigned. This task can now be filled in and completed.')
      onAssigned?.()
    } catch (e) {
      toast.error('Could not assign the form. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const markDoneWithoutForm = async () => {
    setSubmitting(true)
    try {
      await submitTask(taskId)
      toast.success(requiresApproval
        ? 'Task submitted for approval. No form was attached.'
        : 'Task marked as done. Completed without a form.')
      onAssigned?.()
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not complete this task. Please try again.')
      setSubmitting(false)
      setConfirming(false)
    }
  }

  return (
    <Panel ref={panelRef}>
      <IconWrap><DescriptionOutlinedIcon /></IconWrap>
      <Heading>This task has no form attached</Heading>
      <Body>
        You can <strong>add a form</strong> to record answers and sign-off, or
        {' '}<strong>mark it done without one</strong>. If you complete it without a
        form, no form response is recorded against this task.
      </Body>

      {confirming ? (
        <ConfirmBox>
          <ConfirmIcon><CheckCircleOutlineRoundedIcon /></ConfirmIcon>
          <ConfirmText>
            Mark this task as done <strong>without a form</strong>?<br />
            There is no form attached, so <strong>no form response will be recorded</strong>.
            The task will simply be {requiresApproval ? 'sent for approval' : 'completed'}.
          </ConfirmText>
          <Controls>
            <GhostBtn type='button' onClick={() => setConfirming(false)} disabled={submitting}>
              Cancel
            </GhostBtn>
            <DoneBtn type='button' onClick={markDoneWithoutForm} disabled={submitting}>
              {submitting
                ? 'Completing…'
                : (requiresApproval ? 'Submit for approval' : 'Yes, mark as done')}
            </DoneBtn>
          </Controls>
        </ConfirmBox>
      ) : (
        <>
          {canManage && (
            <AssignArea>
              <FormTemplateField
                value={selected}
                template={selectedTemplate}
                onChange={template => {
                  setSelected(template?.id || '')
                  setSelectedTemplate(template || null)
                }}
                buildingPassportId={buildingPassportId}
                programmeObligationId={programmeObligationId}
                disabled={saving}
                emptyLabel='No form chosen yet'
                emptyHint='Choose the form the person doing this work will fill in.'
              />
              <Controls>
                <AssignBtn type='button' onClick={assign} disabled={!selected || saving}>
                  {saving ? 'Adding…' : 'Add this form to the task'}
                </AssignBtn>
              </Controls>
              {buildingPassportId && (
                <FromDocument data-testid='form-gen-nudge-task'>
                  <FromDocumentText>Already have a checklist for this? Upload it and we will turn it into a form.</FromDocumentText>
                  <GenerateFormFromDocument
                    buildingPassportId={buildingPassportId}
                    targetType='task'
                    targetId={taskId}
                  >
                    {open => (
                      <FormGenButton type='button' onClick={open} data-tour-id='formGen.open.task'>
                        <UploadFileRoundedIcon aria-hidden='true' />
                        <span>Create a form from a document</span>
                      </FormGenButton>
                    )}
                  </GenerateFormFromDocument>
                </FromDocument>
              )}
            </AssignArea>
          )}

          {canSubmit && (
            <NoFormArea>
              {canManage && <OrText>or</OrText>}
              <DoneBtn type='button' onClick={() => setConfirming(true)}>
                <CheckCircleOutlineRoundedIcon style={{ fontSize: 18 }} />
                Mark as done. No form needed
              </DoneBtn>
              <NoFormHint>
                This task has no form attached. Completing it won’t record any form response.
              </NoFormHint>
            </NoFormArea>
          )}

          {!canManage && !canSubmit && (
            <Muted>Ask someone who manages this task to add a form, or to mark it done without one.</Muted>
          )}
        </>
      )}
    </Panel>
  )
})

export default AssignFormPanel

const FromDocument = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  margin-top: 4px;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
`

const FromDocumentText = styled.span`
  font-size: 15px;
  line-height: 1.45;
  color: #334155;
`

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
  padding: 28px 20px;
  background: #fff7ed;
  border: 1px solid #ffedd5;
  border-radius: 12px;
`

const IconWrap = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #ffedd5;
  color: #c2410c;

  svg { font-size: 24px; }
`

const Heading = styled.h3`
  margin: 4px 0 0;
  font-size: 16px;
  font-weight: 800;
  color: #7c2d12;
`

const Body = styled.p`
  margin: 0;
  max-width: 480px;
  font-size: 13.5px;
  line-height: 1.55;
  color: #7c2d12;
`

const AssignArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 620px;
  margin: 0 auto;
`

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 8px;
`

const AssignBtn = styled.button`
  padding: 10px 18px;
  border: none;
  border-radius: 999px;
  background: #c2410c;
  color: #fff;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;

  &:disabled { opacity: 0.55; cursor: default; }
`

const NoFormArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
`

const OrText = styled.span`
  font-size: 12.5px;
  color: #9a6a4a;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`

const DoneBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border: none;
  border-radius: 999px;
  background: #15803d;
  color: #fff;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;

  &:hover { background: #166534; }
  &:disabled { opacity: 0.55; cursor: default; }
`

const NoFormHint = styled.p`
  margin: 0;
  max-width: 420px;
  font-size: 12.5px;
  line-height: 1.5;
  color: #9a6a4a;
`

const ConfirmBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-top: 6px;
  padding: 16px 18px;
  background: #fff;
  border: 1px solid #e6cbb0;
  border-radius: 12px;
  max-width: 460px;
`

const ConfirmIcon = styled.div`
  display: inline-flex;
  color: #15803d;
  svg { font-size: 26px; }
`

const ConfirmText = styled.p`
  margin: 0;
  font-size: 13.5px;
  line-height: 1.55;
  color: #334155;
`

const GhostBtn = styled.button`
  padding: 10px 18px;
  border: 1px solid #cbd5e1;
  border-radius: 999px;
  background: #fff;
  color: #475569;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  &:disabled { opacity: 0.55; cursor: default; }
`

const Muted = styled.p`
  margin: 4px 0 0;
  font-size: 13px;
  color: #9a6a4a;
`
